import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { errorText, Icon, PopItem, Popover, usePopover, useToast } from '../ui/kit';
import type { IconName } from '../ui/icons';
import { AUTH_MODE, canApproveExpenses, canWrite, isEmployee, ROLE_NAMES, useSession, useTenant, type Role } from '../auth/session';
import { saveThemeToAccount } from '../lib/schemeSync';
import { applyTheme, useTheme } from '../lib/theme';
import { useTenantDetails } from '../api/hooks';
import { fmtDate } from '../lib/format';
import { AddBusinessModal } from './AddBusiness';
import { readPref, writePref } from '../lib/prefs';
import { SearchPalette } from './SearchPalette';
import { NotificationsButton } from './Notifications';
import { Logo } from '../ui/Logo';

const LOGO = <Logo />;

type NavEntry = { to: string; icon: IconName; label: string } | { group: string; icon: IconName; label: string; items: [string, string][] };
// Only screens the API supports today. The other prototype screens come back as their endpoints
// are built.
const NAV: NavEntry[] = [
  { to: '/', icon: 'dashboard', label: 'Dashboard' },
  { group: 'sales', icon: 'doc', label: 'Sales', items: [['/quotes', 'Quotes'], ['/invoices', 'Invoices'], ['/credit-notes', 'Credit notes'], ['/payments-received', 'Payments received'], ['/customers', 'Customers'], ['/items', 'Items']] },
  { group: 'purchases', icon: 'bill', label: 'Purchases', items: [['/purchase-orders', 'Purchase orders'], ['/bills', 'Bills'], ['/vendor-credits', 'Vendor credits'], ['/payments-made', 'Payments made'], ['/vendors', 'Vendors']] },
  { group: 'expenses', icon: 'card', label: 'Expenses', items: [['/expenses', 'My expenses'], ['/expenses/review', 'To review'], ['/expenses/reimburse', 'Reimburse'], ['/expenses/rates', 'Travel rates']] },
  { group: 'banking', icon: 'bank', label: 'Banking', items: [['/banking', 'Accounts and transactions'], ['/reconcile', 'Reconciliations']] },
  { group: 'accounting', icon: 'scale', label: 'Accounting', items: [['/accounts', 'Chart of accounts'], ['/journals', 'Journal entries'], ['/reports/general-ledger', 'General ledger']] },
  { to: '/reports', icon: 'chart', label: 'Reports' },
  // The business's own papers (ADR 0031). Not in an employee's menu: they never see the vault.
  { to: '/vault', icon: 'folder', label: 'Document vault' },
];
/** An employee's menu: only their own expenses (ADR 0023). Time tracking and payroll join it later. */
const EMPLOYEE_NAV: NavEntry[] = [{ to: '/expenses', icon: 'card', label: 'My expenses' }];
/** Approving and reimbursing are for owners, admins and accountants. */
const APPROVER_ONLY = ['/expenses/review', '/expenses/reimburse', '/expenses/rates'];
export function navFor(role: Role): NavEntry[] {
  if (isEmployee(role)) return EMPLOYEE_NAV;
  if (canApproveExpenses(role)) return NAV;
  return NAV.map((n) => ('items' in n ? { ...n, items: n.items.filter(([to]) => !APPROVER_ONLY.includes(to)) } : n));
}
/** The search shortcut as this keyboard shows it. */
const SHORTCUT = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl K';
const CHEV = <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>;

/** Which nav entry a path belongs to, so detail pages keep their section highlighted. */
function sectionOf(path: string): string {
  if (path.startsWith('/expenses/review')) return '/expenses/review';
  if (path.startsWith('/expenses/reimburse')) return '/expenses/reimburse';
  if (path.startsWith('/expenses')) return '/expenses';
  if (path.startsWith('/expenses/rates')) return '/expenses/rates';
  if (path.startsWith('/invoices')) return '/invoices';
  if (path.startsWith('/quotes')) return '/quotes';
  if (path.startsWith('/purchase-orders')) return '/purchase-orders';
  if (path.startsWith('/bills')) return '/bills';
  if (path.startsWith('/credit-notes')) return '/credit-notes';
  if (path.startsWith('/vendor-credits')) return '/vendor-credits';
  if (path.startsWith('/payments-received') || path.startsWith('/customer-refunds')) return '/payments-received';
  if (path.startsWith('/payments-made') || path.startsWith('/vendor-refunds')) return '/payments-made';
  if (path.startsWith('/customers')) return '/customers';
  if (path.startsWith('/vendors')) return '/vendors';
  if (path.startsWith('/reports/general-ledger')) return '/reports/general-ledger';
  if (path.startsWith('/reports')) return '/reports';
  if (path.startsWith('/reconcile')) return '/reconcile';
  if (path.startsWith('/journals')) return '/journals';
  if (path.startsWith('/banking')) return '/banking';
  if (path.startsWith('/vault')) return '/vault';
  return path;
}

const initial = (name: string) => (name.trim()[0] ?? 'M').toUpperCase();
const COLORS = ['#eb6834', '#1baf7a', '#6250d6', '#e87ba4', '#2a78d6', '#eda100', '#0b7a70'];
export const businessColor = (id: string) => COLORS[parseInt(id.replace(/-/g, '').slice(0, 6), 16) % COLORS.length] as string;

export function Shell() {
  const session = useSession();
  const tenant = useTenant();
  const details = useTenantDetails();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [collapsed, setCollapsed] = useState(readPref('mybooks.menuCollapsed') === '1');
  const [drawer, setDrawer] = useState(false);
  const [groups, setGroups] = useState<Record<string, boolean>>({});
  const [adding, setAdding] = useState(false);
  const [searching, setSearching] = useState(false);
  const tenantPop = usePopover(), createPop = usePopover(), profilePop = usePopover(), helpPop = usePopover(), groupPop = usePopover();
  // The group whose pages the collapsed menu shows beside its icon.
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const key = sectionOf(location.pathname);
  const currency = details.data?.base_currency ?? 'USD';
  const lockedThrough = (details.data as { locked_through?: string | null } | undefined)?.locked_through;
  const employee = isEmployee(tenant.role);
  const nav = navFor(tenant.role);
  const userInitials = (session.user?.name ?? session.user?.email ?? 'U').split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');

  useEffect(() => { setDrawer(false); document.getElementById('view')?.focus({ preventScroll: true }); window.scrollTo(0, 0); }, [location.pathname]);
  // Cmd+K / Ctrl+K opens search from anywhere, as in the prototype. Employees have no search.
  useEffect(() => {
    if (employee) return;
    const onKey = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setDrawer(false); setSearching(true); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [employee]);

  const switchTo = (id: string, name: string) => {
    session.selectTenant(id);
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== id });
    navigate('/');
    toast(`Switched to ${name}`);
  };
  const toggleCollapsed = () => { setCollapsed((c) => { writePref('mybooks.menuCollapsed', c ? '0' : '1'); return !c; }); };

  return (
    <div className={`app ${collapsed ? 'collapsed' : ''} ${drawer ? 'drawer-open' : ''}`}>
      <nav className="rail" aria-label="Shortcuts">
        <NavLink className="rail-brand" to="/" aria-label="MyBooks home">{LOGO}</NavLink>
        <button className="rail-btn menu-btn" onClick={() => setDrawer(true)} aria-label="Open the menu"><Icon name="dashboard" size={20} /></button>
        {employee ? (
          <NavLink className="rail-btn" to="/expenses/new" data-tip="New expense" aria-label="New expense"><Icon name="plus" size={20} /></NavLink>
        ) : (
          <>
            <button className="rail-btn" onClick={() => setSearching(true)} data-tip={`Search  ${SHORTCUT}`} aria-label="Search" aria-haspopup="dialog"><Icon name="search" size={20} /></button>
            <button className="rail-btn" onClick={createPop.toggle} data-tip="Create" aria-label="Create" aria-haspopup="true" aria-expanded="false"><Icon name="plus" size={20} /></button>
            <NotificationsButton />
            <NavLink className={({ isActive }) => `rail-btn ${isActive ? 'active' : ''}`} to="/settings" data-tip="Settings" aria-label="Settings"><Icon name="gear" size={20} /></NavLink>
          </>
        )}
        <button className="rail-btn" onClick={helpPop.toggle} data-tip="Help" aria-label="Help" aria-haspopup="true" aria-expanded="false"><Icon name="help" size={20} /></button>
        <ThemeSwitch />
        <button className="rail-avatar" onClick={profilePop.toggle} aria-label="Your profile" aria-haspopup="true" aria-expanded="false">{userInitials}</button>
      </nav>
      <aside className="sidebar" aria-label="Menu">
        <div className="side-head">
          <button className="tenant-switch" onClick={tenantPop.toggle} aria-haspopup="true" aria-expanded="false" aria-label="Switch business">
            <div className="tenant-avatar" style={{ background: businessColor(tenant.id) }}>{initial(tenant.name)}</div>
            <div style={{ minWidth: 0 }}><div className="tenant-name">{details.data?.name ?? tenant.name}</div><div className="tenant-role">{ROLE_NAMES[tenant.role]} · {currency}</div></div>
            <svg className="updown" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m7 15 5 5 5-5M7 9l5-5 5 5" /></svg>
          </button>
          <button className="collapse-btn" onClick={toggleCollapsed} aria-label="Hide the menu" title="Hide the menu"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg></button>
        </div>
        {!employee && <button type="button" className="search search-btn side-search" onClick={() => { setDrawer(false); setSearching(true); }} aria-haspopup="dialog"><Icon name="search" /><span style={{ flex: 1 }}>Search…</span><kbd>{SHORTCUT}</kbd></button>}
        <nav className="nav" aria-label="Main navigation">
          {nav.map((n) => {
            if (!('items' in n)) return <NavLink key={n.to} end className={`nav-item ${key === n.to ? 'active' : ''}`} to={n.to} aria-current={key === n.to ? 'page' : undefined}><Icon name={n.icon} size={18} />{n.label}</NavLink>;
            // Groups start closed and open only when clicked.
            const open = groups[n.group] === true;
            const hasActive = n.items.some(([to]) => to === key);
            return (
              <div className="nav-group" key={n.group}>
                <button className={`group-btn ${hasActive ? 'has-active' : ''}`} aria-expanded={open} onClick={() => setGroups((g) => ({ ...g, [n.group]: !open }))}><Icon name={n.icon} size={18} />{n.label}{CHEV}</button>
                {open && <div className="nav-sub">{n.items.map(([to, label]) => <NavLink key={to} className={`sub-item ${to === key ? 'active' : ''}`} to={to} aria-current={to === key ? 'page' : undefined}>{label}</NavLink>)}</div>}
              </div>
            );
          })}
        </nav>
        <div className="side-foot">{lockedThrough ? <><Icon name="lock" size={13} />Books locked through {fmtDate(lockedThrough)}</> : null}</div>
        {/* Collapsed on a wide screen, the menu keeps its icons; a group's pages open beside it. */}
        <div className="side-mini">
          <button className="mini-tenant" onClick={tenantPop.toggle} aria-haspopup="true" aria-expanded="false" aria-label={`Switch business (${details.data?.name ?? tenant.name})`} data-tip={details.data?.name ?? tenant.name}>
            <span className="tenant-avatar" style={{ background: businessColor(tenant.id) }}>{initial(tenant.name)}</span>
          </button>
          <button className="expand-btn" onClick={toggleCollapsed} aria-label="Show the menu" title="Show the menu"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg></button>
          <nav className="mini-nav" aria-label="Main navigation, collapsed">
            {nav.map((n) => {
              if (!('items' in n)) return <NavLink key={n.to} end className={`mini-item ${key === n.to ? 'active' : ''}`} to={n.to} aria-label={n.label} data-tip={n.label} aria-current={key === n.to ? 'page' : undefined}><Icon name={n.icon} size={18} /></NavLink>;
              const hasActive = n.items.some(([to]) => to === key);
              return (
                <button key={n.group} type="button" className={`mini-item ${hasActive ? 'active' : ''}`} aria-label={n.label} data-tip={openGroup === n.group && groupPop.anchor ? undefined : n.label} aria-haspopup="true" aria-expanded="false"
                  onClick={(e) => { setOpenGroup(n.group); groupPop.toggle(e); }}><Icon name={n.icon} size={18} /></button>
              );
            })}
          </nav>
          {lockedThrough && <span className="mini-lock" title={`Books locked through ${fmtDate(lockedThrough)}`}><Icon name="lock" size={14} /></span>}
        </div>
      </aside>
      <div className="drawer-scrim" onClick={() => setDrawer(false)} hidden={!drawer} />
      <div className="main">
        <main className="content" id="view" tabIndex={-1}><div className="page">{!employee && canWrite(tenant.role) && <QuickBar />}<Outlet /></div></main>
      </div>

      {tenantPop.anchor && (
        <Popover anchor={tenantPop.anchor} onClose={tenantPop.close} label="Your businesses">
          <div className="pop-title">Your businesses</div>
          {session.tenants.map((t) => (
            <PopItem key={t.id} checked={t.id === tenant.id} onClick={() => { if (t.id !== tenant.id) switchTo(t.id, t.name); }}
              title={t.name} sub={ROLE_NAMES[t.role]} extra={t.id === tenant.id ? <Icon name="check" /> : undefined} />
          ))}
          <div className="pop-sep" />
          {!employee && <PopItem icon="chart" title="Group financial reporting" sub="Entity, combined or consolidated" to="/reports/group" />}
          <PopItem icon="plus" title="Add a business" sub="Its own books, team and settings" onClick={() => setAdding(true)} />
          {session.archived.length > 0 && <div className="pop-sep" />}
          {session.archived.map((a) => (
            <PopItem key={a.id} icon="back" title={`Restore ${a.name}`} sub="Archived. Everything in it is as you left it"
              onClick={() => { tenantPop.close(); session.restoreBusiness(a.id).then(() => { navigate('/'); toast(`${a.name} is back`); }, (e) => toast(errorText(e))); }} />
          ))}
        </Popover>
      )}
      {createPop.anchor && (
        <Popover anchor={createPop.anchor} onClose={createPop.close} label="Create">
          <div className="pop-title">Create</div>
          <PopItem icon="doc" title="Quote" sub="Offer a price before you invoice" to="/quotes/new" />
          <PopItem icon="doc" title="Invoice" sub="Bill a customer" to="/invoices/new" />
          <PopItem icon="bill" title="Bill" sub="Record what you owe a vendor" to="/bills/new" />
          <PopItem icon="box" title="Purchase order" sub="Order goods or services from a vendor" to="/purchase-orders/new" />
          <PopItem icon="doc" title="Credit note" sub="Give a customer credit" to="/credit-notes/new" />
          <PopItem icon="bill" title="Vendor credit" sub="Record credit a vendor gave you" to="/vendor-credits/new" />
          <div className="pop-sep" />
          <PopItem icon="cash" title="Receive payment" sub="Against one or more invoices" to="/payments-received?new=1" />
          <PopItem icon="cash" title="Pay bills" sub="Pay one or more bills of a vendor" to="/payments-made?new=1" />
          <div className="pop-sep" />
          <PopItem icon="people" title="Customer" to="/customers?new=1" />
          <PopItem icon="truck" title="Vendor" to="/vendors?new=1" />
          <PopItem icon="box" title="Item" sub="A product or service you sell" to="/items?new=1" />
          <PopItem icon="scale" title="Journal entry" sub="Adjust the books directly" to="/journals/new" />
          <PopItem icon="card" title="Expense" sub="Claim a receipt you paid" to="/expenses/new" />
          <div className="pop-sep" />
          <PopItem icon="upload" title="Import a bank statement" sub="CSV, OFX, QFX or OFC" to="/banking?import=1" />
        </Popover>
      )}
      {groupPop.anchor && openGroup && (() => {
        const group = nav.find((n) => 'items' in n && n.group === openGroup);
        if (!group || !('items' in group)) return null;
        return (
          <Popover anchor={groupPop.anchor} onClose={groupPop.close} label={group.label}>
            <div className="pop-title">{group.label}</div>
            {group.items.map(([to, label]) => <PopItem key={to} title={label} to={to} checked={to === key} extra={to === key ? <Icon name="check" /> : undefined} />)}
          </Popover>
        );
      })()}
      {helpPop.anchor && (
        <Popover anchor={helpPop.anchor} onClose={helpPop.close} label="Help">
          <div className="pop-title">Help</div>
          <div className="pop-item static"><span className="pop-ic"><Icon name="mail" /></span><span><span className="pi-title" style={{ display: 'block' }}>Email support</span><span className="pi-sub" style={{ display: 'block', userSelect: 'all' }}>support@mybooks.app</span></span></div>
        </Popover>
      )}
      {profilePop.anchor && (
        <Popover anchor={profilePop.anchor} onClose={profilePop.close} label="Your profile">
          <div className="pop-item static"><span className="user-chip">{userInitials}</span><span><span className="pi-title" style={{ display: 'block' }}>{session.user?.name ?? session.user?.email}</span><span className="pi-sub" style={{ display: 'block' }}>{session.user?.email} · {ROLE_NAMES[tenant.role]}</span></span></div>
          <div className="pop-sep" />
          {!employee && <><PopItem icon="gear" title="Business settings" to="/settings" /><PopItem icon="people" title="My account" sub="Team and billing" to="/account" /><div className="pop-sep" /></>}
          <PopItem icon="back" title="Sign out" onClick={() => { void session.signOut(); }} />
        </Popover>
      )}
      {searching && !employee && <SearchPalette onClose={() => setSearching(false)} />}
      {adding && <AddBusinessModal onClose={() => setAdding(false)} onCreated={(name) => { setAdding(false); queryClient.clear(); navigate('/'); toast(`${name} is ready. You are the owner.`); }} />}
    </div>
  );
}

/** Shortcuts to the three things people do most, at the top right of every screen. */
const QUICK: [string, IconName, string][] = [['/invoices/new', 'doc', 'New invoice'], ['/bills/new', 'bill', 'New bill'], ['/reconcile', 'bank', 'Bank reconcile']];
function QuickBar() {
  return (
    <nav className="quick-bar" aria-label="Quick access">
      {QUICK.map(([to, icon, label]) => <NavLink key={to} className="btn small" to={to} aria-label={label} title={label}><Icon name={icon} /><span className="quick-label">{label}</span></NavLink>)}
    </nav>
  );
}

export function Placeholder({ title, children }: { title: string; children?: ReactNode }) {
  return <div className="card empty"><b>{title}</b> {children}</div>;
}

/**
 * Light or dark mode, from the bottom of the rail. One click switches what is on screen; the choice
 * is saved in this browser and, signed in, to the account. Settings → App layout can go back to
 * following the device.
 */
function ThemeSwitch() {
  const { effective } = useTheme();
  const toast = useToast();
  const next = effective === 'dark' ? 'light' : 'dark';
  const label = next === 'dark' ? 'Dark mode' : 'Light mode';
  return (
    <button type="button" className="rail-btn rail-theme" data-tip={label} aria-label={`Switch to ${label.toLowerCase()}`}
      onClick={() => {
        applyTheme(next);
        if (AUTH_MODE === 'supabase') void saveThemeToAccount(next);
        toast(`${label} on`);
      }}>
      <Icon name={next === 'dark' ? 'moon' : 'sun'} size={20} />
    </button>
  );
}
